// The Value and Learn stage bodies under the Advisory strip (polish.P28c,
// #715). Locked comp: `.impeccable/mocks/decision/advisory-stages.html`,
// family A — Value: the driver tree in the 8 with C's partition in its head,
// Captured this shift in the 4 (adjudication 4), the selected issue full-width
// in B's expand layout; Learn: the proposed FMEA changes in the 8, the decision
// on the selected proposal in the 4 with B's "Updated this week" row at its top
// (adjudication 5), and the rule set and its changes as one tabbed card.
// Derivations live in `LateStageBodies.transform.ts` and the driver tree's own
// transform; the recipes are `stage-body-ui.ts`.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { SurfacePagePanel, SurfacePageTabs, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import type { Persona } from '@/shared/types';
import type { FMEAUpdateReview } from '@/versions/v1/schema/case';
import { DriverIssueCard, DriverTreeScene, useDriverTree } from './DriverTree/Scene';
import type { FlowCapturedRow, FlowLearnRow } from './FlowLanding.transform';
import type { ApprovalsRuleSetPanel } from './Approvals.transform';
import { ActorChip } from './PipelineStrip';
import { StageCard } from './StageCard';
import { useWholeRowScroll } from './WholeRowScroll';
import {
  approvalBasisForSeat,
  approvalTabCounts,
  approveButtonLabel,
  capturedPanelMeta,
  capturedRowView,
  finalGateSeat,
  gatePillText,
  learnedRowView,
  learnTabs,
  reviewSeatShort,
  metaPhrases,
  reviewStateParts,
  symptomLines,
  type LearnTabId,
  type RuleSetRead,
} from './LateStageBodies.transform';
import {
  APPROVAL_TABS,
  CAUSE_CATEGORY_LABEL,
  REVIEW_SEAT_LABEL,
  type ApprovalTab,
  type EnrichedFmeaReview,
} from './Scene.transform';
import { GATE_GHOST_BUTTON, GATE_PRIMARY_BUTTON, gatePillClass } from './gate-ui';
import {
  STAGE_FEEDS,
  STAGE_GATE_BOX,
  STAGE_INK_LINK,
  STAGE_LABEL_CAPS,
  STAGE_MONEY,
  STAGE_MONEY_COMPARISON,
  STAGE_ROW_META,
  STAGE_ROW_SELECTED,
  STAGE_ROW_TITLE,
  STAGE_ROWS,
  STAGE_TABLE,
  STAGE_TD,
  STAGE_TD_FIRST,
  STAGE_TH,
  STAGE_TH_FIRST,
  STAGE_THEAD,
} from './stage-body-ui';

// ---------------------------------------------------------------------------
// Value
// ---------------------------------------------------------------------------

/** Beside the 8 the Captured list takes the row's height and scrolls inside it
 *  (the comp's 386 px body); stacked below lg it caps at that height. Either
 *  way its edge ends on a row boundary (`useWholeRowScroll`, polish.P28c r1b);
 *  the classes are the first-paint fallback. */
const CAPTURED_SCROLL = 'relative max-h-[27rem] overflow-auto lg:max-h-none lg:min-h-0 lg:flex-1';
const CAPTURED_FALLBACK_REM = 27;

function CapturedPanel({ rows, showMoney }: { rows: readonly FlowCapturedRow[]; showMoney: boolean }) {
  const scroll = useWholeRowScroll<HTMLUListElement>({ kind: 'fill', fallbackRem: CAPTURED_FALLBACK_REM, minRows: 1 });
  return (
    <Commentable name="Captured">
      <div className="h-full lg:relative">
        <SurfacePagePanel
          title="Captured this shift"
          meta={capturedPanelMeta(rows, showMoney)}
          flush
          className="h-full lg:absolute lg:inset-0"
          contentClassName="flex flex-col pb-0"
        >
          <ul ref={scroll.ref} style={scroll.style} data-stage-body-list="value" className={cn(CAPTURED_SCROLL, STAGE_ROWS)}>
            {rows.map((row) => {
              const view = capturedRowView(row, showMoney);
              return (
                <li key={view.caseId} data-captured-row={view.caseId} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 px-4 py-2">
                  <div className="min-w-0">
                    <p className={cn(STAGE_ROW_TITLE, 'text-[13px]')}>{view.title}</p>
                    <p className={STAGE_ROW_META}>{view.stamp}</p>
                  </div>
                  <div className="max-w-[9rem] text-right tabular-nums">
                    {view.money ? (
                      <>
                        <span className={cn(STAGE_MONEY, 'block')}>{view.money}</span>
                        <span className={cn(STAGE_MONEY_COMPARISON, 'block')}>{view.comparison}</span>
                      </>
                    ) : (
                      <span className={STAGE_FEEDS}>{view.comparison}</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </SurfacePagePanel>
      </div>
    </Commentable>
  );
}

export function ValueBody({
  capturedRows,
  showMoney,
  onOpenCase,
}: {
  capturedRows: readonly FlowCapturedRow[];
  /** Comp adjudication 7: the floor reads no US$ on Value, Captured included. */
  showMoney: boolean;
  onOpenCase: (caseId: string) => void;
}) {
  const read = useDriverTree();
  // The rank-1 issue is open on arrival (B's open-on-arrival row).
  const [selectedId, setSelectedId] = useState<string>(() => read.issues[0]!.id);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 lg:col-span-8">
          <DriverTreeScene read={read} selectedId={selectedId} onSelect={setSelectedId} onOpenCase={onOpenCase} />
        </div>
        <div className="col-span-12 lg:col-span-4">
          <CapturedPanel rows={capturedRows} showMoney={showMoney} />
        </div>
      </div>
      <DriverIssueCard read={read} issueId={selectedId} onOpenCase={onOpenCase} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Learn
// ---------------------------------------------------------------------------

/** The proposals table scrolls inside the comp's 360 px body; beside the
 *  decision it takes the row's height. Its edge ends on a row boundary under
 *  the sticky head (`useWholeRowScroll`, polish.P28c r1b). */
const PROPOSALS_SCROLL = 'max-h-[25rem] overflow-auto lg:max-h-none lg:min-h-0 lg:flex-1';
const PROPOSALS_FALLBACK_REM = 25;

/** A meta line whose phrases never break inside themselves; `last` (the seat a
 *  row waits on, which can be long) wraps freely. */
function Phrases({ text, last }: { text: string; last?: ReactNode }) {
  const phrases = metaPhrases(text);
  // The separator rides on the end of the phrase before it, so a line breaks
  // after a `·`, never before one.
  return (
    <>
      {phrases.map((phrase, index) => (
        <span key={`${index}-${phrase}`}>
          <span className="whitespace-nowrap">{phrase}{index < phrases.length - 1 || last ? ' ·' : null}</span>
          {index < phrases.length - 1 || last ? ' ' : null}
        </span>
      ))}
      {last}
    </>
  );
}

function ReviewStateCell({ label }: { label: string }) {
  const parts = reviewStateParts(label);
  return (
    <Phrases
      text={parts.awaiting ? parts.lead.replace(/ · $/, '') : parts.lead}
      last={parts.awaiting ? <span className="font-semibold text-ink-secondary">{parts.awaiting}</span> : undefined}
    />
  );
}

function FactBlock({ label, action, children }: { label: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="px-4 py-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className={STAGE_LABEL_CAPS}>{label}</span>
        {action}
      </div>
      <div className="mt-0.5 text-[13px] leading-snug text-ink-secondary">{children}</div>
    </div>
  );
}

export type LearnGateProps = {
  persona: Persona;
  /** `fmea:review` on a pending review: the Review form. */
  canReview: boolean;
  /** The seat may approve this review (its persona list, else `fmea:approve`). */
  mayApprove: boolean;
  defaultGate: string;
  onReview: (reviewId: string, notes: string) => void;
  onApprove: (reviewId: string) => void;
  onReject: (reviewId: string, reason: string) => void;
};

function DecisionGate({
  selected,
  gate,
  reviewClock,
}: {
  selected: EnrichedFmeaReview;
  gate: LearnGateProps;
  reviewClock: (stamp: string) => string;
}) {
  const review = selected.review;
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  useEffect(() => {
    setShowReviewForm(false);
    setShowRejectForm(false);
    setReviewNotes('');
    setRejectReason('');
  }, [review.id]);
  const pill = gatePillText(review);
  const pending = review.approvalStatus === 'pending' && review.reviewStatus !== 'rejected';
  const approvable = gate.mayApprove && pending;
  const reviewable = gate.canReview && review.reviewStatus === 'pending';
  return (
    <section
      className={cn(STAGE_GATE_BOX, 'flex flex-col gap-2')}
      aria-label="Decision"
      data-approvals-gate
      data-approvals-gate-state={pill.tone}
    >
      <span className={cn(pill.tone === 'approved' ? gatePillClass('approved') : gatePillClass('draft'), 'self-start')}>{pill.text}</span>
      <p className="text-[13px] font-semibold leading-snug text-ink-primary">{selected.review.proposedChange.summary}</p>
      <ul className="space-y-1.5">
        {review.reviewSeats.map((seat) => {
          const seatLabel = REVIEW_SEAT_LABEL[seat] ?? seat;
          return (
            <li key={seat} className="flex flex-wrap items-center gap-1.5">
              <ActorChip actor="Human">{review.reviewedBy ? `${review.reviewedBy} · ${seatLabel}` : seatLabel}</ActorChip>
              <span className="text-xs tabular-nums text-muted">
                {review.reviewedBy && review.reviewedAt ? `reviewed ${reviewClock(review.reviewedAt)}` : 'Pending review'}
              </span>
            </li>
          );
        })}
      </ul>
      {review.reviewNotes ? <p className="text-xs leading-relaxed text-ink-secondary">{review.reviewNotes}</p> : null}
      {pending && !approvable ? (
        <p data-final-gate className="text-xs text-muted">
          Final gate · <span className="font-semibold text-ink-secondary">{finalGateSeat(review, gate.defaultGate)}</span>
        </p>
      ) : null}
      {reviewable || approvable ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {reviewable ? (
            showReviewForm ? (
              <div className="flex w-full flex-col items-start gap-2">
                <textarea
                  className="block min-h-[4rem] w-full rounded-lg border border-[var(--hairline)] bg-transparent px-2.5 py-1.5 text-xs"
                  placeholder="Review notes"
                  value={reviewNotes}
                  onChange={(event) => setReviewNotes(event.target.value)}
                />
                <button
                  type="button"
                  className={cn(GATE_PRIMARY_BUTTON, 'h-8 px-3')}
                  disabled={!reviewNotes.trim()}
                  onClick={() => {
                    gate.onReview(review.id, reviewNotes);
                    setShowReviewForm(false);
                  }}
                >
                  Submit review
                </button>
              </div>
            ) : (
              <button type="button" className={cn(GATE_PRIMARY_BUTTON, 'h-8 px-3')} onClick={() => setShowReviewForm(true)}>
                Review
              </button>
            )
          ) : null}
          {approvable ? (
            <>
              <button type="button" className={cn(GATE_PRIMARY_BUTTON, 'h-8 px-3')} onClick={() => gate.onApprove(review.id)}>
                {approveButtonLabel(review, gate.persona, gate.defaultGate)}
              </button>
              {showRejectForm ? (
                <div className="flex w-full flex-col items-start gap-2">
                  <textarea
                    className="block min-h-[4rem] w-full rounded-lg border border-[var(--hairline)] bg-transparent px-2.5 py-1.5 text-xs"
                    placeholder="Reason for rejection"
                    value={rejectReason}
                    onChange={(event) => setRejectReason(event.target.value)}
                  />
                  <button
                    type="button"
                    className={cn(GATE_GHOST_BUTTON, 'h-8 px-2.5')}
                    disabled={!rejectReason.trim()}
                    onClick={() => {
                      gate.onReject(review.id, rejectReason);
                      setShowRejectForm(false);
                    }}
                  >
                    Confirm reject
                  </button>
                </div>
              ) : (
                <button type="button" className={cn(GATE_GHOST_BUTTON, 'h-8 px-2.5')} onClick={() => setShowRejectForm(true)}>
                  Reject
                </button>
              )}
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function DecisionCard({
  selected,
  learned,
  ruleSet,
  showMoney,
  gate,
  reviewClock,
  onOpenCase,
}: {
  selected: EnrichedFmeaReview;
  learned: readonly FlowLearnRow[];
  ruleSet: RuleSetRead;
  showMoney: boolean;
  gate: LearnGateProps;
  reviewClock: (stamp: string) => string;
  onOpenCase: (caseId: string) => void;
}) {
  const basis = approvalBasisForSeat(selected.approvalBasis, showMoney);
  const detail = [
    selected.immediateResponse ? { label: 'Immediate response', value: selected.immediateResponse } : null,
    selected.responsible ? { label: 'Responsible', value: selected.responsible } : null,
  ].filter((row): row is { label: string; value: string } => row !== null);
  const hasLogic = Boolean(selected.rule || selected.caseItem?.ifThen);
  const symptom = selected.caseItem ? symptomLines(selected.caseItem) : null;
  return (
    <Commentable name="Decision">
      <div className="h-full">
        <StageCard
          title={`Decision · ${selected.review.id}`}
          className="h-full"
          data-decision-card={selected.review.id}
          actions={selected.caseItem ? (
            <button type="button" className={STAGE_INK_LINK} onClick={() => onOpenCase(selected.caseItem!.id)}>
              open case
            </button>
          ) : undefined}
        >
          {learned.length > 0 ? (
            <div data-learned-this-week className="border-b border-[var(--hairline)] px-4 py-2.5">
              <span className={STAGE_LABEL_CAPS}>Updated this week</span>
              <ul data-stage-body-list="learn" className={cn(STAGE_ROWS, 'mt-0.5')}>
                {learned.map((row) => {
                  const view = learnedRowView(row, ruleSet.version);
                  return (
                    <li key={view.id} data-learned-row={view.id} className="py-1.5 first:pt-0.5 last:pb-0">
                      <p className="text-[13px] font-semibold leading-snug text-ink-primary">{view.summary}</p>
                      <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 font-mono text-[10px] text-muted">
                        <span><Phrases text={view.meta} /></span>
                        <span className={gatePillClass('approved')}>Approved</span>
                      </p>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
          <div className="py-1">
            {selected.caseItem ? (
              <FactBlock label="KPI / symptom">
                <span className="font-medium text-ink-primary">{symptom!.lead}</span>
                {symptom!.detail ? <span className="block text-ink-secondary">{symptom!.detail}</span> : null}
              </FactBlock>
            ) : null}
            {selected.outcome ? <FactBlock label="Root cause">{selected.outcome.diagnosedCause}</FactBlock> : null}
            {basis.map((item) => <FactBlock key={item.label} label={item.label}>{item.value}</FactBlock>)}
            {detail.length > 0 || hasLogic ? (
              <details data-decision-detail className="group px-4 py-1.5">
                <summary className="flex cursor-pointer list-none items-center gap-1 [&::-webkit-details-marker]:hidden">
                  <ChevronRight className="h-3.5 w-3.5 text-muted transition-transform duration-150 group-open:rotate-90" aria-hidden />
                  <span className={STAGE_LABEL_CAPS}>Detection logic and response</span>
                </summary>
                <dl className="mt-1 space-y-1.5 text-xs leading-snug text-ink-secondary">
                  {selected.rule ? (
                    <div>
                      <dt className="text-muted">AI detection logic</dt>
                      <dd className="font-mono text-[11px] text-ink-primary">{selected.rule.signalPattern}</dd>
                      <dd>{selected.rule.diagnosis}</dd>
                    </div>
                  ) : null}
                  {selected.caseItem?.ifThen ? (
                    <div>
                      <dt className="text-muted">IF → THEN</dt>
                      <dd>{selected.caseItem.ifThen}</dd>
                    </div>
                  ) : null}
                  {detail.map((row) => (
                    <div key={row.label}>
                      <dt className="text-muted">{row.label}</dt>
                      <dd>{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </details>
            ) : null}
          </div>
          <div className="px-3 pb-3">
            <DecisionGate selected={selected} gate={gate} reviewClock={reviewClock} />
          </div>
        </StageCard>
      </div>
    </Commentable>
  );
}

/** The proposals table gives its width to the proposed change (comp A: about
 *  354 px at 1470, two lines): Category and State hold fixed narrow columns, so
 *  FMR-0007 reads whole on two lines at 1470; the comp's two-line clamp holds
 *  from 1400 up, and where the 8 is narrower the title wraps whole. */
const PROPOSAL_CATEGORY_COL = 'w-[6.5rem]';
const PROPOSAL_STATE_COL = 'w-[9.5rem] min-w-[8.5rem]';
const PROPOSAL_TITLE = 'text-[13px] font-medium leading-snug text-ink-primary min-[1400px]:line-clamp-2';

export function LearnBody({
  ruleSet,
  approvalPanel,
  learnedRows,
  reviews,
  approvalTab,
  onApprovalTab,
  approvalRows,
  selectedReview,
  onSelectReview,
  stateLabel,
  reviewClock,
  showMoney,
  gate,
  ruleSetBody,
  sessionUpdates,
  sessionBody,
  initialTab,
  onOpenCase,
}: {
  ruleSet: RuleSetRead;
  approvalPanel: ApprovalsRuleSetPanel;
  learnedRows: readonly FlowLearnRow[];
  /** Every review in the session, for the tab headlines. */
  reviews: readonly FMEAUpdateReview[];
  approvalTab: ApprovalTab;
  onApprovalTab: (tab: ApprovalTab) => void;
  approvalRows: readonly EnrichedFmeaReview[];
  selectedReview?: EnrichedFmeaReview;
  onSelectReview: (reviewId: string) => void;
  stateLabel: (review: FMEAUpdateReview) => string;
  reviewClock: (stamp: string) => string;
  showMoney: boolean;
  gate: LearnGateProps;
  ruleSetBody: ReactNode;
  sessionUpdates: number;
  sessionBody: ReactNode;
  initialTab?: LearnTabId;
  onOpenCase: (caseId: string) => void;
}) {
  const counts = useMemo(() => approvalTabCounts(reviews), [reviews]);
  const tabs = learnTabs(ruleSet, approvalPanel, sessionUpdates);
  // A change this session is the newest thing on Learn, so its tab opens first;
  // otherwise the live rule set (the comp's default).
  const [learnTab, setLearnTab] = useState<LearnTabId>(initialTab ?? (sessionUpdates > 0 ? 'session' : 'rules'));
  const activeTab = tabs.some((tab) => tab.id === learnTab) ? learnTab : 'rules';
  const selectedId = selectedReview?.review.id;
  const proposalsScroll = useWholeRowScroll<HTMLDivElement>({ kind: 'fill', fallbackRem: PROPOSALS_FALLBACK_REM, minRows: 1 }, 'tbody > tr');
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-12 gap-3">
        <div className={cn('col-span-12', selectedReview && 'lg:col-span-8 lg:min-h-[30rem]')}>
          <Commentable name="Approvals">
            <div className="h-full lg:relative">
              <StageCard
                title="Proposed FMEA changes"
                beside={<span className={gatePillClass('draft')}>Draft — not live until approved</span>}
                actions={
                  <span className="inline-flex items-center whitespace-nowrap rounded-full bg-[var(--hairline)] px-2 py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em] text-ink-secondary">
                    {`Rule set v${ruleSet.version} · ${ruleSet.live} live · ${ruleSet.proposed} proposed`}
                  </span>
                }
                className={cn('h-full', selectedReview && 'lg:absolute lg:inset-0')}
                bodyClassName="flex flex-col"
              >
                <div className="border-b border-[var(--hairline)] px-3 py-2">
                  <SurfacePageTabs
                    aria-label="Approval history"
                    items={APPROVAL_TABS.map((tab) => ({ ...tab, count: counts[tab.id] }))}
                    value={approvalTab}
                    onChange={onApprovalTab}
                    variant="ink"
                    density="compact"
                  />
                </div>
                <div ref={proposalsScroll.ref} style={proposalsScroll.style} data-approvals-scroll className={PROPOSALS_SCROLL}>
                  <Table clickableRows wrap={false} className={STAGE_TABLE}>
                    <THead className={STAGE_THEAD}>
                      <TR>
                        <TH className={STAGE_TH_FIRST}>Review</TH>
                        <TH className={STAGE_TH}>Proposed change</TH>
                        <TH className={cn(STAGE_TH, PROPOSAL_CATEGORY_COL)}>Category</TH>
                        <TH className={cn(STAGE_TH, PROPOSAL_STATE_COL, 'pr-4')}>State</TH>
                      </TR>
                    </THead>
                    <TBody data-approvals-list>
                      {approvalRows.map((row) => {
                        const selected = row.review.id === selectedId;
                        return (
                          <TR
                            key={row.review.id}
                            data-review-id={row.review.id}
                            data-selected={selected || undefined}
                            className={selected ? STAGE_ROW_SELECTED : undefined}
                            onRowClick={() => onSelectReview(row.review.id)}
                          >
                            <TD className={cn(STAGE_TD_FIRST, 'whitespace-nowrap align-top')}>
                              <div className="font-mono text-xs font-semibold text-ink-primary">{row.review.id}</div>
                              <div className="mt-0.5 text-[11px] leading-snug text-muted">{reviewSeatShort(row.review)}</div>
                            </TD>
                            <TD className={cn(STAGE_TD, 'align-top')}>
                              <div title={row.review.proposedChange.summary} className={PROPOSAL_TITLE}>{row.review.proposedChange.summary}</div>
                            </TD>
                            <TD className={cn(STAGE_TD, PROPOSAL_CATEGORY_COL, 'align-top text-[11px] leading-snug text-muted')}>
                              {CAUSE_CATEGORY_LABEL[row.review.causeCategory] ?? row.review.causeCategory}
                            </TD>
                            <TD className={cn(STAGE_TD, PROPOSAL_STATE_COL, 'whitespace-normal pr-4 align-top text-[11px] leading-snug text-muted')}>
                              <ReviewStateCell label={stateLabel(row.review)} />
                            </TD>
                          </TR>
                        );
                      })}
                    </TBody>
                  </Table>
                </div>
              </StageCard>
            </div>
          </Commentable>
        </div>
        {selectedReview ? (
          <div className="col-span-12 lg:col-span-4">
            <DecisionCard
              selected={selectedReview}
              learned={learnedRows}
              ruleSet={ruleSet}
              showMoney={showMoney}
              gate={gate}
              reviewClock={reviewClock}
              onOpenCase={onOpenCase}
            />
          </div>
        ) : null}
      </div>

      <Commentable name="RuleSet">
        <section className="card-matte-2 flex min-w-0 flex-col" data-learn-tabs>
          <div className="border-b border-[var(--hairline)] px-3 py-2">
            <SurfacePageTabs
              aria-label="Rule set"
              items={tabs}
              value={activeTab}
              onChange={setLearnTab}
              variant="ink"
              density="compact"
            />
          </div>
          {activeTab === 'rules' ? ruleSetBody : null}
          {activeTab === 'changes' ? (
            <ul data-rule-set-changes className={STAGE_ROWS}>
              {approvalPanel.rows.map((row, index) => (
                <li key={`${row.kind}-${index}`} className="px-4 py-2 text-[13px] text-ink-secondary">
                  <span className={row.kind === 'added' ? 'font-semibold text-[var(--state-online)]' : 'font-semibold text-ink-primary'}>{row.kind}</span>
                  {' · '}{row.label}
                </li>
              ))}
            </ul>
          ) : null}
          {activeTab === 'session' ? sessionBody : null}
        </section>
      </Commentable>
    </div>
  );
}
